<template>
  <div>
    <div class="data-top">
      <interval-date-picker
        v-if="showDatePicker"
        :year="false"
        :has-min-date="isPeriodMin"
        @date-updated="date = $event"
        @end-date-updated="endDate = $event"
        @interval-updated="interval = $event"
      />
    </div>

    <v-tabs
      v-model="currentTabIdx"
      show-arrows
      background-color="transparent"
      class="elevation-0"
    >
      <v-tab
        v-for="comp in filteredComps"
        :key="`tab-for-${comp.name}`"
        @click="changeTab"
      >
        {{ comp.text }}
      </v-tab>
    </v-tabs>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />
    <v-tabs-items
      v-else
      v-model="currentTabIdx"
      touchless
      style="background: transparent"
    >
      <v-tab-item
        v-for="comp in filteredComps"
        :key="comp.name"
      >
        <keep-alive>
          <component
            :is="comp.name"
            :key="`${comp.name}-${receiptUpdateCount}-${date}-${interval}-${endDate}`"
            :date="date"
            :interval="interval"
            :end-date="endDate"
            @toggle-form="isFormOpen = $event"
            @open-receipt="getReceipt($event)"
            @revisit="getRevisitTable($event)"
          />
        </keep-alive>
      </v-tab-item>
    </v-tabs-items>

    <receipt-show-drawer
      v-if="receipt && receipt.id && receipt.meta && receipt.attributes"
      :is-visible="!isLoadingReceipt && isReceiptOpen"
      :receipt-id="receipt.id"
      :table-name="receipt.meta.tableName"
      :start-at="receipt.meta.tableStartAt"
      :sequence-number="receipt.meta.sequenceNumber"
      :customer-count="receipt.meta.tableCustomerCount"
      :table-id="receipt.attributes.tableId"
      @close="closeReceipt"
      @deleted="deleteReceipt"
      @updated="receiptUpdateCount += 1"
    />
    <table-revisit-drawer
      v-if="table && table.id"
      :is-visible="isRevisitReceiptOpen"
      :hall-id="table.attributes.hallId"
      :receipt-id="table.attributes.receipt.data.id"
      :table-id="table.id"
      :table-number="table.attributes.tableNumber"
      :table-prefix="table.attributes.tablePrefix"
      :table-suffix="table.attributes.tableSuffix"
      :custom-name="table.meta && table.meta.tableName ? table.meta.tableName : ''"
      :courses="table.meta.tableInitConfig.data.attributes.courses.data"
      :customer-count="table.attributes.customerCount"
      :customer-table-taggings="table.attributes.customerTableTaggings.data"
      :customer-ids="table.attributes.customerIds || []"
      :halls="halls"
      :referrals="referrals"
      :referring-histories="referringHistories"
      :nominations="nominations"
      :nomination-histories="nominationHistories"
      :vending-contribution-triggers="vendingContributionTriggers"
      @updated:customer-count="table.attributes.customerCount = $event"
      @updated:customer-table-taggings="table.attributes.customerTableTaggings.data = $event"
      @updated:hall-id-table-number="updateHallIdTableNumber($event)"
      @close="closeRevisitReceipt"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  provide,
  getCurrentInstance,
  inject,
} from '@vue/composition-api'
import { groupBy } from 'lodash'
import useCurrentData from '@/views/composable/useCurrentData'
import useQueryString from '@/views/composable/useQueryString'
import useCompInit from '@/views/composable/useCompInit'
import UserApi from '@/api/waiter/User'
import VendibleApi from '@/api/waiter/Vendible'
import ReferralApi from '@/api/waiter/Referral'
import ReceiptApi from '@/api/waiter/Receipt'
import HallApi from '@/api/waiter/Hall'
import TableApi from '@/api/waiter/Table'
import VendingContributionTriggerApi from '@/api/waiter/VendingContributionTrigger'
import TableData from '@/views/components/data/Tables.vue'
import ReferringHistoriesData from '@/views/components/data/ReferringHistories.vue'
import VendingHistoriesData from '@/views/components/data/VendingHistories.vue'
import ReceiptsData from '@/views/components/data/Receipts.vue'
import BalanceReceivablesData from '@/views/components/data/BalanceReceivables.vue'
import TimecardsData from '@/views/components/data/Timecards.vue'
import CustomerTagsData from '@/views/components/data/CustomerTagsData.vue'
import SequenceNumbersData from '@/views/components/data/SequenceNumbers.vue'
import IntervalDatePicker from '@/views/components/util/filter/IntervalDatePicker.vue'
import ReceiptShowDrawer from '@/views/components/receipt/ReceiptShowDrawer.vue'
import TableRevisitDrawer from '@/views/components/table/TableRevisitDrawer/TableRevisitDrawer.vue'
import { useAbilityEvaluate } from '@/views/v2-temp/common/composables/useAbilityEvaluate'

export default {
  components: {
    IntervalDatePicker,
    TableData,
    ReferringHistoriesData,
    VendingHistoriesData,
    ReceiptsData,
    BalanceReceivablesData,
    TimecardsData,
    CustomerTagsData,
    SequenceNumbersData,
    ReceiptShowDrawer,
    TableRevisitDrawer,
  },
  props: {
    showDatePicker: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { route, addQuery, resetQuery } = useQueryString()
    const { currentClub } = useCurrentData()
    const { canAccessPeriod, canAccessReceiptSequenceNumbers } = useAbilityEvaluate()
    const { initWith, isLoading } = useCompInit()
    const currentTabIdx = ref(0)
    const isFormOpen = ref(false)
    const users = ref([])
    const courses = ref([])
    const nominations = ref([])
    const items = ref([])
    const vendibles = ref([])
    const referrals = ref([])
    const vendingContributionTriggers = ref([])
    const referringHistories = ref([])
    const nominationHistories = ref([])
    const isLoadingReceipt = ref(false)
    const isReceiptOpen = ref(false)
    const isRevisitReceiptOpen = ref(false)
    const receipt = ref({})
    const receiptUpdateCount = ref(0)
    const currentClubMeta = inject('currentClubMeta', { today: new Date().toISOString().substr(0, 10) })
    const date = ref(route.value.query.date || currentClubMeta.today)
    const interval = ref(Number(route.value.query.interval) || 0)
    const endDate = ref(route.value.query.endDate)

    const halls = ref([])
    const table = ref({})

    provide('users', users)
    provide('courses', courses)
    provide('nominations', nominations)
    provide('items', items)
    provide('vendibles', vendibles)
    provide('referrals', referrals)
    provide('referringHistories', referringHistories)
    provide('nominationHistories', nominationHistories)
    provide('vendingContributionTriggers', vendingContributionTriggers)
    provide('receipt', receipt)
    provide('isLoadingReceipt', isLoadingReceipt)
    provide('isReceiptOpen', isReceiptOpen)
    const changeTab = () => {
      isFormOpen.value = false

      const routeQuery = {
        ...(route.value.query.date ? { date: route.value.query.date } : {}),
        ...(route.value.query.interval ? { interval: route.value.query.interval } : {}),
        ...(route.value.query.endDate ? { endDate: route.value.query.endDate } : {}),
      }
      resetQuery(true)
      addQuery(routeQuery, true)
    }

    const getHalls = async () => {
      const res = await HallApi.getHalls({ isOperation: true })

      if (res?.data) {
        halls.value = [...res.data.halls.data]
      }
    }

    const getUsers = async () => {
      const res = await UserApi.getUsers()

      if (res?.status === 200) {
        users.value = [...res.data.users.data]
      }
    }

    const getVendibles = async () => {
      const res = await VendibleApi.getVendibles()

      if (res?.status === 200) {
        vendibles.value = [...res.data.vendibles.data]

        const vendiblesByType = groupBy(vendibles.value, 'attributes.vendibleType')
        courses.value = vendiblesByType.Course
        nominations.value = vendiblesByType.Nomination
        items.value = vendiblesByType.Item
      }
    }

    const getReferrals = async () => {
      const res = await ReferralApi.getReferrals()

      if (res?.status === 200) {
        referrals.value = [...res.data.referrals.data]
      }
    }

    const getReceipt = async id => {
      receipt.value = { id }
      isLoadingReceipt.value = true

      const res = await ReceiptApi.getReceipt(id)

      if (res?.status === 200) {
        receipt.value = { ...res.data.receipt.data }

        isReceiptOpen.value = true
      }

      isLoadingReceipt.value = false
    }

    const getRevisitTable = async tableId => {
      const res = await TableApi.getTable(tableId)

      isLoadingReceipt.value = true

      if (res?.status === 200) {
        table.value = res.data.data

        referringHistories.value = res.data.data.attributes.referringHistories.data

        nominationHistories.value = res.data.data.attributes.vendingHistories.data.filter(
          vendible => {
            return vendible.attributes.vendibleType === 'Nomination'
          },
        )

        isRevisitReceiptOpen.value = true
      }

      isLoadingReceipt.value = false
    }

    const getVendingContributionTriggers = async () => {
      const res = await VendingContributionTriggerApi.getVendingContributionTriggers()

      if (res?.status === 200) {
        vendingContributionTriggers.value = [...res.data.vendingContributionTriggers.data]
      }
    }

    const closeReceipt = () => {
      isReceiptOpen.value = false
      receipt.value = {}
    }

    const deleteReceipt = () => {
      closeReceipt()
      receiptUpdateCount.value += 1
      vm.$toast.success('伝票を削除しました')
    }

    const closeRevisitReceipt = () => {
      isRevisitReceiptOpen.value = false
      table.value = {}
    }

    const updateHallIdTableNumber = hallIdTableNumber => {
      const [hallId, tableNumber] = hallIdTableNumber.split('-')
      table.value.attributes.hallId = Number(hallId)
      table.value.attributes.tableNumber = Number(tableNumber)
    }

    const isPeriodMin = computed(() => !canAccessPeriod())

    const comps = [
      {
        name: 'TableData',
        text: '卓',
      },
      {
        name: 'ReferringHistoriesData',
        text: `${currentClub.value?.referralAlias || 'リファラル'}履歴`,
      },
      {
        name: 'VendingHistoriesData',
        text: '販売履歴',
      },
      {
        name: 'ReceiptsData',
        text: '伝票',
      },
      {
        name: 'BalanceReceivablesData',
        text: '売掛',
      },
      {
        name: 'TimecardsData',
        text: 'タイムカード',
      },
      {
        name: 'CustomerTagsData',
        text: 'タグ',
      },
      {
        name: 'SequenceNumbersData',
        text: '連番管理表',
      },
    ]

    const filteredComps = computed(() => {
      return comps.filter(comp => {
        if (comp.name === 'SequenceNumbersData') {
          return canAccessReceiptSequenceNumbers()
        }

        return true
      })
    })

    initWith([
      getHalls(),
      getUsers(),
      getVendibles(),
      getReferrals(),
      getVendingContributionTriggers(),
    ])

    return {
      // data
      currentTabIdx,
      isLoading,
      isFormOpen,
      canAccessReceiptSequenceNumbers,
      filteredComps,
      users,
      halls,
      vendibles,
      nominations,
      nominationHistories,
      referrals,
      referringHistories,
      vendingContributionTriggers,
      receipt,
      isReceiptOpen,
      isLoadingReceipt,
      receiptUpdateCount,
      date,
      endDate,
      interval,
      isRevisitReceiptOpen,
      table,

      // methods
      changeTab,
      getReceipt,
      getRevisitTable,
      closeReceipt,
      deleteReceipt,
      closeRevisitReceipt,
      updateHallIdTableNumber,

      isPeriodMin,
    }
  },
}
</script>
